import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
// @ts-ignore wait for react router update
import { useNavigate } from "react-router-dom";
import { get, noop } from "lodash";
import { Button, Icon, IconButton, MtpPro } from "@tvg/design-system";
import { RaceProgram, RaceVideoFeedback } from "@tvg/ts-types/Race";
import { NullaryFn } from "@tvg/ts-types/Functional";
import { events as AlchemerEvents } from "@urp/alchemer";
import { easings, useSpring } from "@react-spring/web";
import {
  Container,
  Wrapper,
  TrackNameContainer,
  RaceNumberContainer,
  TrackInfoWrapper,
  TrackInfoContainer,
  IconWrapper,
  TitleComputationContainer
} from "./styled-components";
import { getTrackHeaderEnhacement } from "../../../../store/selectors";
import AccountBalance from "../../AccountBalance";
import VideoButton from "../../VideoButton";
import { Status } from "../../../../types";
import LoginRegButton from "../../LoginRegButton";
import { isFdr } from "../../../../utils/general";
import { useWindowScrollPosition } from "./utils";

interface MobileHeaderProps {
  isLogged?: boolean;
  balance: number;
  race: RaceProgram;
  prevPath?: string;
  openLogin?: NullaryFn<void>;
  openRegister?: NullaryFn<void>;
  onAccBalanceClick?: NullaryFn<void>;
  title: string;
  videoFeedback?: RaceVideoFeedback;
  mtp: number;
  status: Status;
  postTime: string;
  onClickVideo?: NullaryFn<void>;
  onClickFavorite?: NullaryFn<void>;
  withVideo?: boolean;
  isFavoriteLoading?: boolean;
}

const scaleAnimation = 0.98;
const positionAnimation = 7;

const MobileHeader = ({
  isLogged,
  balance,
  race,
  title,
  videoFeedback,
  mtp,
  status,
  postTime,
  prevPath = "/home",
  openLogin = noop,
  openRegister = noop,
  onAccBalanceClick = noop,
  onClickVideo = noop,
  onClickFavorite = noop,
  withVideo,
  isFavoriteLoading
}: MobileHeaderProps) => {
  const navigate = useNavigate();
  const [textRef, setTextRef] = useState<HTMLDivElement | null>(null);
  const [wrapperRef, setWrapperRef] = useState<HTMLDivElement | null>(null);
  const hasTrackHeaderEnhacement = useSelector(getTrackHeaderEnhacement);
  const [displayTitle, setDisplayTitle] = useState("");
  const [maxWidth, setMaxWidth] = useState(0);

  useEffect(() => {
    if (!maxWidth && wrapperRef && wrapperRef.clientWidth) {
      setMaxWidth(wrapperRef.clientWidth);
    }

    if (maxWidth && textRef && textRef.clientWidth) {
      if (textRef.clientWidth >= maxWidth) {
        setDisplayTitle(`${race.track?.trackCode} - `);
      } else {
        setDisplayTitle(title);
      }
    }
  }, [wrapperRef, textRef, maxWidth]);

  const storedFavoriteTracks = useSelector((store) =>
    get(store, "userFavorites.tracks", [])
  );

  const maxScrollPosition = 162; // This is the height of the entire header
  const scrollPosition = useWindowScrollPosition(100);
  const [firstLoad, setFirstLoad] = useState(true);
  const [collapsedHeader, setCollapsedHeader] = useState(false);

  const config = { duration: 300, easing: easings.easeInOutCirc };

  const raceTitleHeaderConfig = () => ({
    opacity: collapsedHeader ? 0 : 1,
    y: collapsedHeader ? -positionAnimation : 0,
    scale: collapsedHeader ? scaleAnimation : 1,
    config
  });
  const raceSelectionHeaderConfig = () => ({
    opacity: collapsedHeader ? 1 : 0,
    y: collapsedHeader ? 0 : positionAnimation,
    scale: collapsedHeader ? 1 : scaleAnimation,
    config
  });

  const [raceTitleHeaderStyles, raceTitleHeaderApi] = useSpring(
    raceTitleHeaderConfig
  );
  const [raceSelectionHeaderStyles, raceSelectionHeaderApi] = useSpring(
    raceSelectionHeaderConfig
  );

  useEffect(() => {
    raceTitleHeaderApi.start(raceTitleHeaderConfig());
    raceSelectionHeaderApi.start(raceSelectionHeaderConfig());
  }, [collapsedHeader]);

  useEffect(() => {
    if (firstLoad) {
      setFirstLoad(false);
      setCollapsedHeader(false);
    } else {
      setCollapsedHeader(scrollPosition > maxScrollPosition);
    }
  }, [scrollPosition, firstLoad]);

  const getHeaderButtons = (isLoggedIn: boolean, isFdrApp: boolean) => {
    if (isFdrApp) {
      return (
        <>
          <IconWrapper
            style={
              collapsedHeader
                ? raceSelectionHeaderStyles
                : raceTitleHeaderStyles
            }
          >
            {hasTrackHeaderEnhacement && (
              <VideoButton
                videoFeedback={videoFeedback}
                onClickWatchVideo={onClickVideo}
                withVideo={withVideo}
                isIconOnly={collapsedHeader}
                isHidden={!isLoggedIn && collapsedHeader}
              />
            )}
            {!hasTrackHeaderEnhacement && isLoggedIn && (
              <Button
                qaLabel="favorite-button"
                variant="tertiary"
                size="m"
                onPress={onClickFavorite}
                isMobile
                isDisabled={isFavoriteLoading}
              >
                <Icon
                  name="star"
                  size="m"
                  lineColor="white.900"
                  backgroundColor={
                    storedFavoriteTracks.length &&
                    storedFavoriteTracks.includes(race.track?.trackCode)
                      ? "white.900"
                      : "white.100"
                  }
                />
              </Button>
            )}
          </IconWrapper>
          {isLoggedIn && (
            <AccountBalance
              balance={balance}
              onAccBalanceClick={onAccBalanceClick}
            />
          )}
        </>
      );
    }

    return !isFdrApp ? (
      <LoginRegButton loginAction={openLogin} registerAction={openRegister} />
    ) : null;
  };

  return (
    <Container data-qa-label="mobile-header-container" isFdr={isFdr()}>
      <IconButton
        qaLabel="back-button"
        variant="tertiary"
        iconName="chevronLeft"
        tertiaryColor="white.900"
        size="s"
        onPress={() => {
          AlchemerEvents.backTapped();
          navigate(prevPath?.includes("/live") ? "/live" : prevPath);
        }}
      />
      {collapsedHeader && (
        <>
          <TrackInfoWrapper
            style={
              collapsedHeader
                ? raceSelectionHeaderStyles
                : raceTitleHeaderStyles
            }
          >
            <TrackInfoContainer
              ref={(node: HTMLDivElement | null) => setWrapperRef(node)}
            >
              {!displayTitle && (
                <TitleComputationContainer
                  ref={(node: HTMLDivElement | null) => setTextRef(node)}
                >
                  {title}&nbsp;R{race.raceNumber}
                </TitleComputationContainer>
              )}
              {displayTitle && (
                <>
                  <TrackNameContainer>{displayTitle}</TrackNameContainer>
                  <RaceNumberContainer>
                    &nbsp;R{race.raceNumber}
                  </RaceNumberContainer>
                </>
              )}
            </TrackInfoContainer>

            {displayTitle && (
              <MtpPro
                mtp={mtp}
                status={status}
                postTime={postTime}
                data-qa-label="mtp-status"
                isCollapsed
                hasFullText
              />
            )}
          </TrackInfoWrapper>
        </>
      )}
      <Wrapper>{getHeaderButtons(!!isLogged, isFdr())}</Wrapper>
    </Container>
  );
};

export default MobileHeader;
